import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';

/** A predictable, fit-first viewer. Magnification uses native scrolling on touch and desktop. */
class DrbImageViewer extends Component {
  #index = 0;
  #scale = 1;
  #opener;
  #drag;
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog, stage } = this.refs;
    dialog.addEventListener('close', () => {
      this.reset();
      const activeImageButton = this.closest('media-gallery')?.querySelector('slideshow-slide[aria-hidden="false"] .drb-gallery-expand');
      (activeImageButton ?? this.#opener)?.focus({ preventScroll: true });
    }, { signal });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.close();
    }, { signal });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.previous();
      else if (event.key === 'ArrowRight') this.next();
      else if (event.key === '+' || event.key === '=') this.zoomIn();
      else if (event.key === '-') this.zoomOut();
      else if (event.key === '0') this.reset();
      else return;
      event.preventDefault();
    }, { signal });
    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.#scale === 1) return;
      this.#drag = { x: event.clientX, y: event.clientY, left: stage.scrollLeft, top: stage.scrollTop };
      stage.setPointerCapture(event.pointerId);
      stage.classList.add('is-dragging');
      event.preventDefault();
    }, { signal });
    stage.addEventListener('pointermove', (event) => {
      if (!this.#drag) return;
      stage.scrollLeft = this.#drag.left - (event.clientX - this.#drag.x);
      stage.scrollTop = this.#drag.top - (event.clientY - this.#drag.y);
    }, { signal });
    const stopDrag = () => {
      this.#drag = null;
      stage.classList.remove('is-dragging');
    };
    stage.addEventListener('pointerup', stopDrag, { signal });
    stage.addEventListener('pointercancel', stopDrag, { signal });
    stage.addEventListener('lostpointercapture', stopDrag, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  open(index = 0, event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.refs.dialog.open) return;
    this.#opener = document.activeElement;
    this.refs.dialog.showModal();
    this.#select(Number(index));
  }

  close() {
    this.refs.dialog.close();
  }

  #select(index) {
    const items = this.refs.media;
    if (!items.length) return;
    this.#index = (index + items.length) % items.length;
    items.forEach((item, position) => {
      item.hidden = position !== this.#index;
      if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause());
    });
    this.refs.counter.textContent = `${this.#index + 1} / ${items.length}`;
    this.reset();
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#index));
  }

  previous() { this.#select(this.#index - 1); }
  next() { this.#select(this.#index + 1); }
  zoomIn() { this.#setScale(this.#scale + 0.5); }
  zoomOut() { this.#setScale(this.#scale - 0.5); }
  reset() { this.#setScale(1); }

  // Kept compatible with media-gallery's optional preload hook.
  loadHighResolutionImage() {}

  #setScale(value) {
    const { stage, media, zoomIn, zoomOut, reset } = this.refs;
    const isImage = media[this.#index]?.dataset.mediaType === 'image';
    const nextScale = isImage ? Math.max(1, Math.min(3, value)) : 1;
    const ratio = nextScale / this.#scale;
    const centerX = (stage.scrollLeft + stage.clientWidth / 2) * ratio;
    const centerY = (stage.scrollTop + stage.clientHeight / 2) * ratio;
    this.#scale = nextScale;
    stage.style.setProperty('--viewer-scale', String(nextScale));
    const image = media[this.#index]?.querySelector(':scope > img');
    if (image) image.sizes = `${Math.ceil(stage.clientWidth * nextScale)}px`;
    stage.classList.toggle('is-zoomed', nextScale > 1);
    stage.scrollLeft = nextScale === 1 ? 0 : centerX - stage.clientWidth / 2;
    stage.scrollTop = nextScale === 1 ? 0 : centerY - stage.clientHeight / 2;
    zoomIn.disabled = !isImage || nextScale === 3;
    zoomOut.disabled = nextScale === 1;
    reset.textContent = nextScale === 1 ? 'Fit' : `${Math.round(nextScale * 100)}%`;
  }
}

if (!customElements.get('drb-image-viewer')) customElements.define('drb-image-viewer', DrbImageViewer);
